import React from 'react';
import { Settings, ShieldAlert, Cpu, Activity, RefreshCw } from 'lucide-react';

interface MaintenancePageProps {
    title?: string;
    message?: string;
}

export const MaintenancePage: React.FC<MaintenancePageProps> = ({ 
    title = "System Calibration In Progress", 
    message = "We are currently performing infrastructure updates. Registry access will resume shortly." 
}) => {
    return (
        <div className="fixed inset-0 z-[1000] bg-slate-950 flex flex-col items-center justify-center p-6 text-center overflow-hidden">
            {/* Background Atmosphere */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
                <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-medical-600/30 blur-[150px] rounded-full animate-pulse"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-600/30 blur-[150px] rounded-full animate-pulse [animation-delay:2s]"></div>
            </div>

            <div className="max-w-2xl w-full space-y-12 relative z-10 animate-fade-in">
                <div className="relative inline-block">
                    <div className="w-24 h-24 md:w-32 md:h-32 bg-slate-900 rounded-[2.5rem] border border-white/10 flex items-center justify-center mx-auto shadow-2xl relative z-10 group">
                        <Settings size={56} className="text-medical-400 animate-[spin_8s_linear_infinite] group-hover:text-white transition-colors" />
                    </div>
                    <div className="absolute -top-2 -right-2 p-2 bg-indigo-600 rounded-full shadow-lg border-2 border-slate-950 z-20">
                        <ShieldAlert size={20} className="text-white" />
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="inline-flex items-center gap-3 px-5 py-2 bg-white/5 rounded-full border border-white/10 shadow-xl backdrop-blur-md">
                        <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Administrative Lockout Active</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter leading-tight drop-shadow-2xl">
                        {title}
                    </h1>
                    <p className="text-slate-400 text-lg md:text-xl font-medium leading-relaxed italic max-w-lg mx-auto opacity-90">
                        "{message}"
                    </p>
                </div>

                <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row items-center justify-center gap-10 opacity-30 grayscale transition-all hover:opacity-100 hover:grayscale-0">
                    <div className="flex items-center gap-3">
                        <Cpu size={18} className="text-medical-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Registry: US-EAST</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Activity size={18} className="text-indigo-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Node Calibration</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <RefreshCw size={18} className="text-emerald-400 animate-spin" style={{ animationDuration: '3s' }} />
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Auto-Sync Pending</span>
                    </div>
                </div>

                <div className="pt-12">
                    <button 
                        onClick={() => window.location.reload()}
                        className="px-10 py-4 bg-white text-slate-950 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl hover:bg-emerald-50 transition-all active:scale-95 border-b-4 border-slate-200"
                    >
                        Check Connectivity
                    </button>
                </div>
            </div>
        </div>
    );
};